import type { MixpanelUserProperties } from "./mixpanel_generated/user_properties";
import { getProvider } from "./registry";

export function setUserProperties(props: Partial<MixpanelUserProperties>): void {
  getProvider()?.setUserProperties(props);
}

export function identifyUser(distinctId: string): void {
  getProvider()?.identify(distinctId);
}

/**
 * Identify the user, first resetting if we are switching away from a DIFFERENT already-identified
 * user. This keeps two distinct accounts from being merged into one Mixpanel profile, and lets the
 * new `identify()` start from a clean `$device` id so the merge succeeds — a leftover numeric id is
 * otherwise rejected as `errAnonDistinctIdNonGUIDForm`. The reset is skipped when:
 *   - anonymous (no id, or a `$device:` id) — that path SHOULD merge anonymous history into the user, or
 *   - re-identifying the SAME id — idempotent restored-session recovery.
 *
 * `onResetReseed` re-registers the constant super properties that `reset()` wipes (app-specific,
 * e.g. `product_type` + `device`); a mid-session reset does not pass through the logout re-seed path.
 */
export function identifyWithReset(
  distinctId: string,
  onResetReseed?: () => void,
): void {
  const provider = getProvider();
  if (!provider) return;
  // getDistinctId() can hand back a NUMBER (mixpanel persists whatever id was last identified, and a
  // numeric user id round-trips as a number), so coerce before any string op / compare — a raw number
  // would throw "current.startsWith is not a function" and crash the identify path.
  const currentRaw = provider.getDistinctId?.() ?? null;
  const current = currentRaw == null ? null : String(currentRaw);
  const isAnonymous = !current || current.startsWith("$device:");
  if (!isAnonymous && current !== distinctId) {
    provider.reset();
    onResetReseed?.();
  }
  provider.identify(distinctId);
}

export function resetAnalytics(): void {
  getProvider()?.reset();
}
